import React, { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminLogin, fetchSubmissions, fetchStats } from "@/lib/api";
import { VersaLogo } from "@/components/site/Logo";
import { toast } from "sonner";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("vc_admin_token") || "");
  const [pwd, setPwd] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (tk = token, kind = filter) => {
    if (!tk) return;
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        fetchStats(tk),
        fetchSubmissions(tk, kind || undefined),
      ]);
      setStats(s); setItems(list.items || []);
    } catch (e) {
      toast.error("Session expired. Please login again.");
      setToken(""); localStorage.removeItem("vc_admin_token"); sessionStorage.removeItem("vc_admin_token");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [filter]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await adminLogin(pwd);
      setToken(r.token);
      sessionStorage.setItem("vc_admin_token", r.token);
      toast.success("Logged in");
      load(r.token);
    } catch {
      toast.error("Invalid password");
    }
  };

  const logout = () => {
    setToken(""); setItems([]); setStats({});
    sessionStorage.removeItem("vc_admin_token");
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-5" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-2xl border border-neutral-200 p-7 shadow-sm">
          <div className="flex justify-center mb-6"><VersaLogo size={44} /></div>
          <h1 className="font-display text-2xl font-bold text-center">Admin Console</h1>
          <p className="text-sm text-neutral-500 text-center mt-1">Sign in to view form submissions.</p>
          <Input
            data-testid="admin-pwd"
            type="password"
            placeholder="Admin password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="mt-6"
          />
          <Button type="submit" data-testid="admin-login-btn" className="w-full mt-4 vc-gradient text-white border-0">Sign in</Button>
          <p className="text-[10px] text-neutral-400 text-center mt-4 font-mono-vc">
            Default password: versacorp-admin-2026
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <VersaLogo size={38} />
          <Button variant="outline" onClick={logout} data-testid="admin-logout">Logout</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <h1 className="font-display text-3xl font-extrabold text-neutral-900">Submissions</h1>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          {[
            { k: "total", l: "Total" },
            { k: "quote", l: "Quotes" },
            { k: "sample", l: "Samples" },
            { k: "consultation", l: "Consults" },
            { k: "cup_custom", l: "Customizations" },
          ].map((c) => (
            <div key={c.k} className="rounded-2xl bg-white border border-neutral-200 p-4">
              <div className="text-xs text-neutral-500 font-mono-vc uppercase tracking-wider">{c.l}</div>
              <div className="font-display text-2xl font-bold mt-1">{stats[c.k] ?? 0}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {["", "quote", "sample", "consultation", "contact", "cup_custom"].map((k) => (
            <button
              key={k || "all"}
              onClick={() => setFilter(k)}
              data-testid={`admin-filter-${k || "all"}`}
              className={`px-4 h-9 rounded-full text-sm font-semibold border ${
                filter === k ? "vc-gradient text-white border-transparent" : "bg-white border-neutral-200"
              }`}
            >
              {k ? k.replace("_", " ") : "All"}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl bg-white border border-neutral-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-neutral-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">No submissions yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email / Phone</th>
                  <th className="px-4 py-3 font-semibold">Details</th>
                  <th className="px-4 py-3 font-semibold">Received</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <SubmissionRow key={it.id} it={it} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}


const HIDDEN_KEYS = ["name", "email", "phone", "logo_data_url"];

function SubmissionRow({ it }) {
  const details = useMemo(
    () =>
      Object.entries(it.data || {})
        .filter(([k]) => !HIDDEN_KEYS.includes(k))
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n"),
    [it.data]
  );
  const receivedAt = useMemo(
    () => new Date(it.created_at).toLocaleString(),
    [it.created_at]
  );
  return (
    <tr className="border-t border-neutral-100 align-top" data-testid={`row-${it.id}`}>
      <td className="px-4 py-3">
        <span className="text-xs font-mono-vc uppercase tracking-wider px-2 py-1 rounded-md bg-neutral-100">{it.kind}</span>
      </td>
      <td className="px-4 py-3 font-medium">{it.data?.name}</td>
      <td className="px-4 py-3 text-neutral-600">
        <div>{it.data?.email}</div>
        <div className="text-xs text-neutral-500">{it.data?.phone}</div>
      </td>
      <td className="px-4 py-3 text-neutral-700">
        <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed max-w-md">{details}</pre>
        {it.data?.logo_data_url && (
          <a href={it.data.logo_data_url} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs underline text-orange-600">
            View uploaded logo
          </a>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-neutral-500 font-mono-vc whitespace-nowrap">{receivedAt}</td>
    </tr>
  );
}
